import React, { Suspense } from 'react';
import { LucideIcon } from 'lucide-react';
import { ComponentType } from 'react';

// Custom Cursor icon component using the official SVG
const CursorIcon: React.FC<{ className?: string; width?: number; height?: number }> = ({ 
  className, 
  width = 24, 
  height = 24 
}) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={width} 
    height={height} 
    viewBox="0 0 32 32"
    className={className}
  >
    <path fill="#444" d="M3.75 9v14h24.5V9L16 2"/>
    <path fill="#939393" d="M16 16V2L3.75 9l24.5 14L16 30L3.75 23"/>
    <path fill="#e3e3e3" d="M28.25 9H16v21"/>
    <path fill="#fff" d="M3.75 9h24.5L16 16"/>
  </svg>
);

// Custom API icon component using the provided SVG
const APIIcon: React.FC<{ className?: string; width?: number; height?: number }> = ({ 
  className, 
  width = 24, 
  height = 24 
}) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={width} 
    height={height} 
    viewBox="0 0 48 48"
    className={className}
  >
    <path 
      fill="none" 
      stroke="currentColor" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      d="M38.5 5.5h-29a4 4 0 0 0-4 4v29a4 4 0 0 0 4 4h29a4 4 0 0 0 4-4v-29a4 4 0 0 0-4-4m-5 13.362v10.276" 
      strokeWidth="1"
    />
    <path 
      fill="none" 
      stroke="currentColor" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      d="M23.972 29.138V18.862h3.364c1.902 0 3.444 1.545 3.444 3.451s-1.542 3.451-3.444 3.451h-3.364m-3.792-.03h-4.553M14.5 29.138l3.404-10.276l3.404 10.276" 
      strokeWidth="1"
    />
  </svg>
);

// Custom MCP icon component using the provided SVG
const MCPIcon: React.FC<{ className?: string; width?: number; height?: number }> = ({ 
  className, 
  width = 24, 
  height = 24 
}) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={width} 
    height={height} 
    viewBox="0 0 16 16"
    className={className}
  >
    <path 
      fill="currentColor" 
      d="M5.52 1.12a3.578 3.578 0 0 1 6.078 2.98a3.578 3.578 0 0 1 2.982 6.08l-3.292 3.293a.25.25 0 0 0 0 .354l.843.843a.749.749 0 1 1-1.06 1.06l-.844-.843a1.75 1.75 0 0 1 0-2.474L13.52 9.12a2.08 2.08 0 0 0 0-2.94a2.08 2.08 0 0 0-2.94 0L7.731 9.03A.75.75 0 0 1 6.67 7.97l2.85-2.85a2.08 2.08 0 0 0 0-2.94a2.08 2.08 0 0 0-2.94 0l-4.799 4.8A.75.75 0 0 1 .72 5.92Z"
    />
    <path 
      fill="currentColor" 
      d="M7.52 3.12a.749.749 0 1 1 1.06 1.06L5.731 7.03A2.079 2.079 0 0 0 8.67 9.97l2.85-2.85a.749.749 0 1 1 1.06 1.06l-2.849 2.85A3.578 3.578 0 0 1 4.67 5.97Z"
    />
  </svg>
);

interface IntegrationIconProps {
  icon: string | LucideIcon | ComponentType<any>;
  className?: string;
  size?: number | string;
}

// Brand icon registry with React.lazy loading (ui-main pattern)
const BrandIconRegistry = {
  anthropic: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:anthropic" {...props} />
    }))
  ),
  openai: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:openai" {...props} />
    }))
  ),
  slack: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:slack" {...props} />
    }))
  ),
  discord: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:discord" {...props} />
    }))
  ),
  telegram: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:telegram" {...props} />
    }))
  ),
  microsoftteams: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:microsoftteams" {...props} />
    }))
  ),
  whatsapp: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:whatsapp" {...props} />
    }))
  ),
  zapier: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:zapier" {...props} />
    }))
  ),
  make: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:make" {...props} />
    }))
  ),
  n8n: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:n8n" {...props} />
    }))
  ),
  dify: React.lazy(() =>
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:dify" {...props} />
    }))
  ),
  ifttt: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:ifttt" {...props} />
    }))
  ),
  notion: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:notion" {...props} />
    }))
  ),
  google: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:google" {...props} />
    }))
  ),
  github: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:github" {...props} />
    }))
  ),
  cursor: React.lazy(() => 
    Promise.resolve({
      default: (props: any) => <CursorIcon {...props} />
    })
  ),
  googlechrome: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:googlechrome" {...props} />
    }))
  ),
  ios: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:apple" {...props} />
    }))
  ),
  twitter: React.lazy(() => 
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:twitter" {...props} />
    }))
  ),
  api: React.lazy(() => 
    Promise.resolve({
      default: (props: any) => <APIIcon {...props} />
    })
  ),
  mcp: React.lazy(() => 
    Promise.resolve({
      default: (props: any) => <MCPIcon {...props} />
    })
  ),
  obsidian: React.lazy(() =>
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:obsidian" {...props} />
    }))
  ),
  python: React.lazy(() =>
    import('@iconify/react').then(mod => ({
      default: (props: any) => <mod.Icon icon="simple-icons:python" {...props} />
    }))
  ),
} as const;

type BrandIconName = keyof typeof BrandIconRegistry;

// Professional fallback component (ui-main pattern)
const IconFallback: React.FC<{ iconName: string; className?: string; size?: number | string }> = ({ 
  iconName, 
  className = "w-5 h-5", 
  size = 24 
}) => {
  const brandColors: Record<string, string> = {
    anthropic: '#D97706', // Anthropic orange-ish color for better visibility
    openai: '#10A37F',
    slack: '#4A154B',
    discord: '#5865F2',
    telegram: '#26A5E4',
    microsoftteams: '#6264A7',
    whatsapp: '#25D366',
    zapier: '#FF4A00',
    make: '#8B5CF6',
    n8n: '#EA4B71', // n8n brand color
    dify: '#1C64F2',
    ifttt: '#33C3F0',
    notion: '#000000',
    google: '#4285F4',
    github: '#181717',
    cursor: '#000000',
    twitter: '#1DA1F2',
    api: '#009688',
    mcp: '#000000',
    obsidian: '#7C3AED',
    python: '#3776AB',
  };

  const color = brandColors[iconName] || '#6B7280';
  const letter = iconName.charAt(0).toUpperCase();
  
  return (
    <div 
      className={`${className} rounded flex items-center justify-center text-white font-bold`}
      style={{ 
        width: typeof size === 'number' ? size : 24, 
        height: typeof size === 'number' ? size : 24, 
        backgroundColor: color,
        fontSize: typeof size === 'number' ? size * 0.5 : '12px'
      }}
    >
      {letter}
    </div>
  );
};

/**
 * IntegrationIcon component using ui-main pattern
 * - React.lazy loading for performance
 * - Suspense support with professional fallbacks
 * - Consistent API across different icon types
 */
export function IntegrationIcon({ icon, className = "w-5 h-5", size = 24 }: IntegrationIconProps) {
  // Handle string icon names with lazy loading
  if (typeof icon === 'string') {
    if (icon in BrandIconRegistry) {
      const LazyIcon = BrandIconRegistry[icon as BrandIconName];
      
      return (
        <Suspense fallback={<IconFallback iconName={icon} className={className} size={size} />}>
          <LazyIcon 
            className={className}
            width={typeof size === 'number' ? size : 24}
            height={typeof size === 'number' ? size : 24}
            onError={() => {
              console.warn(`Failed to load icon: ${icon}`);
            }}
          />
        </Suspense>
      );
    } else {
      console.warn(`Unknown icon requested: ${icon}. Available icons:`, Object.keys(BrandIconRegistry));
      // Fallback for unknown icons
      return <IconFallback iconName={icon} className={className} size={size} />;
    }
  }
  
  // Legacy support - Lucide icons
  if (typeof icon === 'function') {
    const LucideComponent = icon as LucideIcon;
    return (
      <LucideComponent
        className={className}
        size={typeof size === 'number' ? size : undefined}
      />
    );
  }
  
  // Final fallback
  return <IconFallback iconName="unknown" className={className} size={size} />;
}

export default IntegrationIcon; 